import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { BehaviorSubject, catchError, Observable, Subject, tap, throwError } from 'rxjs';

import { App, Project } from '../../models';
import { ReportDefinition } from '../../models/report-definition';
import { SNACKBAR_CONSTANTS } from '../../shared/constants';
import { SidesheetComponentOptions } from '../../shared/enums';
import { ReportsDefinitionDataService } from '../data-services';
import { SidesheetUtilityService } from '../utility-services';

@Injectable({
    providedIn: 'root',
})
export class ReportDefinitionsLogicService {
    private _reportDefinitions: ReportDefinition[] = [];
    private _sidesheetSubmitted = new Subject<ReportDefinition>();
    private _sidesheetSubmissionFailed = new Subject<void>();

    reportDefinitions: BehaviorSubject<ReportDefinition[]> = new BehaviorSubject<ReportDefinition[]>(null);
    reportDefinition: BehaviorSubject<ReportDefinition> = new BehaviorSubject<ReportDefinition>(null);
    newReportDefinition: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    reportDefinitionLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    currentReportDefinition: ReportDefinition;
    sidesheetReportDefinition: ReportDefinition = null;

    get sidesheetSubmitted$(): Observable<ReportDefinition> {
        return this._sidesheetSubmitted.asObservable();
    }

    get sidesheetSubmissionFailed$(): Observable<void> {
        return this._sidesheetSubmissionFailed.asObservable();
    }

    constructor(
        private _snackBar: MatSnackBar,
        private _reportDefinitionData: ReportsDefinitionDataService,
        private _sidesheetUtilityService: SidesheetUtilityService,
    ) {
        if (sessionStorage.getItem('reportDefinition')) {
            const reportDefinition = JSON.parse(sessionStorage.getItem('reportDefinition'));
            this.setReportDefinition(reportDefinition);
        }
    }

    openReportDefinitionSidesheet(reportDefinition: ReportDefinition = null): void {
        this.sidesheetReportDefinition = reportDefinition;
        this._sidesheetUtilityService.openSidesheet(SidesheetComponentOptions.ReportDefinitionForm, { disableExternalClose: true });
    }

    submitReportDefinitionSidesheet(reportDefinition: ReportDefinition): void {
        this._sidesheetSubmitted.next(reportDefinition);
    }

    setReportDefinition(reportDefinition: ReportDefinition): void {
        this.setCurrentReportDefinition(reportDefinition);
        sessionStorage.setItem('reportDefinition', JSON.stringify(reportDefinition));
    }

    private setCurrentReportDefinition(reportDefinition: ReportDefinition): void {
        this.reportDefinition.next(reportDefinition);
        this.currentReportDefinition = reportDefinition;
    }

    resetReportDefinitions(): void {
        this.reportDefinitions.next(this._reportDefinitions);
    }

    setReportDefinitions(reportDefinitions: any): void {
        sessionStorage.setItem('reportDefinitions', JSON.stringify(reportDefinitions));
        this.reportDefinitions.next(reportDefinitions);
    }

    createReportDefinition(app: App, project: Project, reportDefinition: ReportDefinition): Observable<ReportDefinition> {
        return this._reportDefinitionData.createReportDefinition(app, project, reportDefinition)
            .pipe(
                tap((result: ReportDefinition) => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Report Definition', 'created'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                    this.updateReportDefinitionLocally(result);
                    this.getReportDefinitionsByProjId(app, project);
                    this._sidesheetUtilityService.closeSidesheet();
                }),
                catchError((error) => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Report Definition', 'creating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                    this._sidesheetSubmissionFailed.next();
                    return throwError(() => error);
                })
            );
    }

    updateReportDefinition(app: App, project: Project, reportDefinition: ReportDefinition): Observable<ReportDefinition> {
        return this._reportDefinitionData.updateReportDefinition(app, project, reportDefinition)
            .pipe(
                tap((result: ReportDefinition) => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Report Definition', 'updated'), SNACKBAR_CONSTANTS.CLOSE, {
                        duration: 5000,
                    });
                    this.updateReportDefinitionLocally(result);
                    this.getReportDefinitionsByProjId(app, project);
                    this._sidesheetUtilityService.closeSidesheet();
                }),
                catchError((error) => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Report Definition', 'updating'), SNACKBAR_CONSTANTS.CLOSE, {
                        duration: 5000,
                    });
                    this._sidesheetSubmissionFailed.next();
                    return throwError(() => error);
                })
            );
    }

    toggleReportDefinitionActive(app: App, project: Project, reportDefinition: ReportDefinition): void {
        this._reportDefinitionData.updateReportDefinition(app, project, reportDefinition)
            .subscribe(
                {
                    next: (result) => {
                        this.updateReportDefinitionLocally(result);
                        this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_TOGGLE_ACTIVE('Report Definition', result.name, result.active), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                    },
                    error: () => {
                        this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Report Definition', 'updating'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                    }
                }
            ).add(() => {
                this.reportDefinitionLoading.next(false);
            });
    }

    removeReportDefinitionFromProject(app: App, project: Project, reportDefinition: ReportDefinition): void {
        this._reportDefinitionData.removeReportDefinitionFromProject(app, project, reportDefinition)
            .subscribe({
                next: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('Report Definition', 'deleted'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000, });
                    this.updateReportDefinitionLocally(reportDefinition);
                    this.getReportDefinitionsByProjId(app, project);
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Report Definition', 'deleting'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000, });
                }
            });
    }

    getReportDefinitionsByProjId(app: App, project: Project): void {
        if (!project.id) {
            return;
        }

        this.reportDefinitions.next(null);
        this.reportDefinitionLoading.next(true);

        this._reportDefinitionData.getReportDefinitionsByProjId(app, project)
            .subscribe({
                next: (response: any) => {
                    this._reportDefinitions = response;
                    this.setReportDefinitions(response);
                },
                error: () => {
                    this._reportDefinitions = [];
                    this.disconnectReportDefinition();
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Report Definition', 'getting', true), SNACKBAR_CONSTANTS.CLOSE, { duration: 2000, });
                }
            })
            .add(() => {
                this.reportDefinitionLoading.next(false);
            });
    }

    updateReportDefinitionLocally(reportDefinition: ReportDefinition): void {
        if (this._reportDefinitions.find((u) => u.id === reportDefinition.id)) {
            const index = this._reportDefinitions.findIndex((u) => u.id === reportDefinition.id);
            this._reportDefinitions[index] = reportDefinition;
        }
        else {
            this._reportDefinitions.push(reportDefinition);
        }
        this.resetReportDefinitions();
    }

    disconnectReportDefinition(): void {
        this.setCurrentReportDefinition(null);
        sessionStorage.removeItem('reportDefinition');
    }

    reportDefinitionLoadingError(): void {
        this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Report Definition', 'loading'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000, });
    }

    reportViewLoadingError(): void {
        this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('Report View', 'loading'), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000, });
    }
}
